import { Component, Injector, NgZone, OnDestroy, OnInit } from '@angular/core';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { assets } from '../../core/constants/assets.const';
import { SharedModule } from '../../shared/shared.module';
import { TranslateModule } from '@ngx-translate/core';
import { AuthService } from '../../services/auth.service';
import { BaseClass } from '../../shared/components/base-class.component';
import { Subject, Subscription, take, takeUntil } from 'rxjs';
import { LoginResponse } from '../../core/constants/loginResponse.model';
import { SwalAlertService } from '../../services/swalAlert.service';
import { environment } from '../../../environments/environment';
import { responseStatus } from '../../core/constants/responseStatus.const';
import { CommonResponse } from '../../models/common.model';
import { NotificationResponse } from '../../models/notification.model';
import { TimeagoPipe } from '../../core/pipe/timeago.pipe';
import { SocketService } from '../../services/socket.service';
import { NotificationService } from '../../services/notification.service';
import { requestStatusKeysConst } from '../../models/requestStatus.const';
import { MatTooltipModule } from '@angular/material/tooltip';

@Component({
  selector: 'tusangaire-main',
  standalone: true,
  imports: [
    MatSidenavModule,
    MatIconModule,
    MatMenuModule,
    CommonModule,
    RouterModule,
    SharedModule,
    TranslateModule,
    MatTooltipModule,
    TimeagoPipe
  ],
  templateUrl: './main.component.html',
  styleUrl: './main.component.scss'
})
export class MainComponent extends BaseClass implements OnDestroy, OnInit {
  protected assets = assets;
  protected sidenavWidth = 200;
  protected destroy$: Subject<boolean> = new Subject<boolean>();
  protected profile: LoginResponse | null = null;
  protected subscriptionSubject$!: Subscription;
  protected notificationListing: any | null = null;
  protected showDot: boolean = false;

  constructor(injector: Injector,
    public _authService: AuthService,
    private _logoutAlert: SwalAlertService,
    public _notifyService: NotificationService,
    private ngZone: NgZone,
    private _socketService: SocketService) {
    super(injector);
  }

  ngOnInit(): void {
    this.subscriptionSubject$ = this._authService.profileDetails
      .pipe(takeUntil(this.destroy$))
      .subscribe((res: LoginResponse | null) => {
        if (res !== null) {
          this.profile = res;
        } else {
          this.getProfile();
        }
      });
    this.subscriptionSubject$ = this._notifyService.showDot
      .pipe(takeUntil(this.destroy$))
      .subscribe((res: boolean) => {
        this.ngZone.run(() => {
          this.getNotification();
          this.showDot = res;
        });
      });
    this.getNotification();
    this._socketService.initSocket();
    this.listenToJoinRoomOk();
  }

  getProfile(): void {
    this.spinnerService.addToLoader('getProfile');
    this._authService
      .getProfile()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: LoginResponse) => {
          this.spinnerService.removeFromLoader('getProfile');
          this.profile = res;
          this._authService.profileDetails.next(res);
        },
        error: () => {
          this.spinnerService.removeFromLoader('getProfile');
        }
      });
  }

  getNotification(): void {
    this.spinnerService.addToLoader('getNotification');
    this._authService
      .getHeaderNotification()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: NotificationResponse) => {
          this.spinnerService.removeFromLoader('getNotification');
          this.notificationListing = res.notification.splice(0, 5);
        },
        error: () => {
          this.spinnerService.removeFromLoader('getNotification');
        }
      });
  }

  toggleSidenav(): void {
    this._authService.toggleSidebarView();
  }

  private listenToJoinRoomOk(): void {
    this._socketService.onEvent('booking_ok').pipe(take(1)).subscribe({
      next: (res: any) => {
        if (res) {
          this.toasterService.successToastr(this.translateService.instant('newBooking'));
        }

      },
      error: (err: any) => {
        console.error('Socket listener error:', err);
      }
    });
  }
  logout(): void {
    this._logoutAlert
      .swalAlert('logout')
      .then((res: { value: boolean; isConfirmed: boolean }) => {
        if (res.value && res.isConfirmed) {
          this._authService
            .logout()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: (res: CommonResponse) => {
                if (res.statusCode === responseStatus.SUCCESS) {
                  localStorage.removeItem(environment.storageKey);
                  this.toasterService.successToastr(
                    this.translateService.instant('Loggedoutsuccessfully')
                  );
                  this.router.navigate(['/']);
                }
              }
            });
        }
      });
  }

  hideDot(): void {
    this.showDot = false;
  }

  getStatus(): string {
    const status = Object.keys(requestStatusKeysConst).find(key => requestStatusKeysConst[key as keyof typeof requestStatusKeysConst] === this.profile?.status);
    if (status === 'ACCEPTED' && this.profile?.isDocExpired) {
      return 'documentExpired';
    } else if (status === 'ACCEPTED' && !this.profile?.isDocExpired) {
      return '';
    }
    return status ? status : '';
  }

  getStatusClass(): string {
    const status = this.getStatus();
    switch (status) {
    case 'documentExpired':
      if (this.profile?.isDocExpired) {
        return 'document-Expired';
      } else {
        return '';
      }
      break;
    case 'REJECTED':
      return 'status-rejected';
    case 'PENDING':
      return 'status-pending';
    default:
      return '';
    }
  }


  redirectToPage(data: any): void {
    switch (true) {
    case data.pushType === 1:
      this.router.navigate(['/bookings/list/0/1']);
      break;
    case data.pushType === 5:
      this.router.navigate(['/bookings/list/0/1']);
      break;
    case data.pushType === 6:
      this.router.navigate(['/homestay/list/1']);
      break;
    case data.pushType === 7:
      this.router.navigate(['/packages/list/0/1']);
      break;
    case data.pushType === 8:
      this.router.navigate(['/service-provider/list/1']);
      break;
    case data.pushType === 9:
      this.router.navigate(['/manage-customers/list/1']);
      break;
    case data.pushType === 10:
      this.router.navigate(['/chat']);
      break;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.complete();
    this.destroy$.unsubscribe();
  }
}
